import { createPropertyToFilter } from './Support';
import {
  DashboardComponentNames,
  filterNotEmptyValues,
  FiltersToolbar as QuickFiltersToolbar,
  IDashboardComponentProperties,
  IDashboardDataGridProperties,
  IFlow,
  ILayout,
  IQuickFiltersWithValues,
  makeDynamicReportingPageStore,
  PINNED_DASHBOARD_KEY,
  processDateRangeFiltersValuesWithPresets,
  useFilterQueryManager,
  useFilters,
  usePerformancePageStore,
  usePrevious,
} from '@diana/core';
import { MutableRefObject, useEffect, useRef } from 'react';
import { SxProps } from '@mui/material';
import { uniq } from 'lodash';

export function FiltersToolbar(props: IFiltersToolbarProps) {
  const {
    reportId,
    layoutData,
    flows,
    filtersToolbarSx = {},
    manageButtonsSx = {},
    needToShowManageDashboardButtons,
    isContentLoading,
    gridApiRef,
    syncFiltersWithUrl = true,
  } = props;
  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const availableFilters = useReportingPageStore((store) => store.availableFilters);
  const filtersValues = useReportingPageStore((store) => store.filtersValues);
  const setFiltersValues = useReportingPageStore((store) => store.setFiltersValues);
  const setAvailableFilters = useReportingPageStore((store) => store.setAvailableFilters);
  const filters = useReportingPageStore((store) => store.filters);
  const setFilters = useReportingPageStore((store) => store.setFilters);
  const hasSetDefaultQuickFiltersValues = useRef(false);
  const reports = usePerformancePageStore((store) => store.reports);
  const report = reports?.find((i) => i.id === reportId.replace(PINNED_DASHBOARD_KEY, ''));
  const prevReportId = usePrevious(reportId);

  const { setQueryParamsForFilters } = useFilterQueryManager();
  const { normalizeFiltersValues, defaultData, onFilterChange, needToRenderAIFilter } = useFilters({
    flows,
    createPropertyToFilter,
    layoutData: report,
    filtersValues,
    setFiltersValues,
    availableFilters,
    withDefaultFiltersValuesFromFlow: false,
    withoutQuickFilters: true,
  });

  useEffect(() => {
    if (prevReportId !== reportId) {
      hasSetDefaultQuickFiltersValues.current = false;
    }
  }, [prevReportId, reportId]);

  useEffect(() => {
    if (availableFilters.length && !hasSetDefaultQuickFiltersValues.current) {
      const result = layoutData?.quickFilterProperty?.keys ?? ({} as IQuickFiltersWithValues);
      const filtersValuesFromLayout = normalizeFiltersValues({
        filtersValues: result,
        availableFilters,
      });
      const processedFiltersValues = processDateRangeFiltersValuesWithPresets({
        filtersValues: {
          ...filterNotEmptyValues(filtersValuesFromLayout),
          ...filterNotEmptyValues(filtersValues),
        },
        availableFilters,
      });
      setFiltersValues(processedFiltersValues);
      setFilters(
        uniq([...Object.keys({ ...filtersValuesFromLayout, ...filtersValues }), ...filters]),
      );
      if (syncFiltersWithUrl) {
        setQueryParamsForFilters({ filtersValues: processedFiltersValues, availableFilters });
      }
      hasSetDefaultQuickFiltersValues.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableFilters,
    layoutData?.quickFilterProperty?.keys,
    setFiltersValues,
    setFilters,
    syncFiltersWithUrl,
  ]);

  return (
    <QuickFiltersToolbar
      filters={filters}
      setFilters={setFilters}
      availableFilters={availableFilters}
      setAvailableFilters={setAvailableFilters}
      filtersValues={filtersValues}
      setFiltersValues={setFiltersValues}
      needToRenderAIFilter={needToRenderAIFilter}
      onFilterChange={onFilterChange}
      defaultData={defaultData}
      filtersToolbarSx={filtersToolbarSx}
      manageButtonsSx={manageButtonsSx}
      defaultQueryFilters={layoutData?.defaultQueryFilters}
      needToShowManageDashboardButtons={needToShowManageDashboardButtons}
      reportId={reportId}
      isContentLoading={isContentLoading}
      gridApiRef={gridApiRef}
      showAddButton={!layoutData?.restrictFilters}
    />
  );
}

interface IFiltersToolbarProps {
  reportId: string;
  layoutData: ILayout<
    IDashboardComponentProperties | IDashboardDataGridProperties,
    DashboardComponentNames
  >;
  flows: IFlow[];
  filtersToolbarSx?: SxProps;
  manageButtonsSx?: SxProps;
  needToShowManageDashboardButtons?: boolean;
  isContentLoading?: boolean;
  height?: number;
  gridApiRef?: MutableRefObject<any>;
  syncFiltersWithUrl?: boolean;
}
